import { useEffect, useRef, useState, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { TILE_URL, TILE_ATTR } from "@/lib/mapTiles";
import { money, dateWithAgo } from "@/lib/format";
import { Navigation, Info, ChevronLeft, ChevronRight, LocateFixed, Home, Store, Radar, Maximize, Crosshair } from "lucide-react";

const SHARJAH = [25.3463, 55.4209];

function markerIcon(kind, active) {
  const base = "border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:#fff";
  const ring = active ? "box-shadow:0 0 0 4px rgba(37,99,235,.35),0 2px 8px rgba(0,0,0,.4);" : "";
  if (kind === "home") {
    const s = active ? 34 : 28;
    return L.divIcon({ className: "", iconSize: [s, s], iconAnchor: [s / 2, s / 2],
      html: `<div style="width:${s}px;height:${s}px;border-radius:8px;background:#111827;${base};${ring}">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/></svg></div>` });
  }
  if (kind === "mine") {
    const s = active ? 30 : 24;
    return L.divIcon({ className: "", iconSize: [s, s], iconAnchor: [s / 2, s / 2],
      html: `<div style="width:${s}px;height:${s}px;border-radius:7px;background:#047857;${base};${ring}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><path d="M3 9l1-5h16l1 5"/><path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9"/></svg></div>` });
  }
  // prospect
  const s = active ? 26 : 20;
  return L.divIcon({ className: "", iconSize: [s, s], iconAnchor: [s / 2, s / 2],
    html: `<div style="width:${s}px;height:${s}px;border-radius:50%;background:#1E3A8A;${base};${ring};font-size:10px;font-weight:900">${active ? "●" : ""}</div>` });
}

const meIcon = L.divIcon({ className: "", iconSize: [18, 18], iconAnchor: [9, 9],
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#2563EB;border:3px solid #fff;box-shadow:0 0 0 6px rgba(37,99,235,.25)"></div>` });

export default function MapExplorer({ garages = [], homeBase, onOpenDetail, heightClass = "h-[62vh] min-h-[380px]", testId = "map-explorer" }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const clusterRef = useRef(null);
  const meRef = useRef(null);
  const markers = useRef({});
  const rowRefs = useRef({});
  const [activeId, setActiveId] = useState(null);
  const located = useMemo(() => garages.filter((g) => g.latitude != null && g.longitude != null), [garages]);

  useEffect(() => {
    if (mapRef.current || !el.current) return;
    const map = L.map(el.current, { zoomAnimation: false, maxZoom: 16 }).setView(SHARJAH, 11);
    L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 19 }).addTo(map);
    layerRef.current = new L.FeatureGroup().addTo(map);
    clusterRef.current = L.markerClusterGroup({
      showCoverageOnHover: false, maxClusterRadius: 45, spiderfyOnMaxZoom: true, chunkedLoading: true,
    });
    map.addLayer(clusterRef.current);
    mapRef.current = map;
    const t = setTimeout(() => mapRef.current && map.invalidateSize(), 250);
    return () => { clearTimeout(t); map.stop(); map.off(); map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition((pos) => {
      const map = mapRef.current; if (!map) return;
      const ll = [pos.coords.latitude, pos.coords.longitude];
      if (!meRef.current) meRef.current = L.marker(ll, { icon: meIcon, zIndexOffset: 1000 }).addTo(map).bindPopup("You are here");
      else meRef.current.setLatLng(ll);
    }, () => {}, { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 });
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // draw markers
  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current, cluster = clusterRef.current;
    if (!map || !layer || !cluster) return;
    layer.clearLayers();
    cluster.clearLayers();
    markers.current = {};

    if (homeBase?.latitude != null) {
      L.marker([homeBase.latitude, homeBase.longitude], { icon: markerIcon("home", false), zIndexOffset: 500 })
        .addTo(layer).bindPopup("Home base — AL JAMHOUR AL MUMAYZ");
    }
    located.forEach((g) => {
      const kind = g._kind || (g.status === "group_created" ? "mine" : "prospect");
      const m = L.marker([g.latitude, g.longitude], { icon: markerIcon(kind, g.id === activeId) });
      m.on("click", () => setActiveId(g.id));
      cluster.addLayer(m);
      if (m.getElement) setTimeout(() => m.getElement()?.setAttribute("data-testid", `map-pin-${g.id}`), 0);
      markers.current[g.id] = { m, kind };
    });

    const pts = [...located.map((g) => [g.latitude, g.longitude])];
    if (homeBase?.latitude != null) pts.push([homeBase.latitude, homeBase.longitude]);
    if (pts.length) {
      const b = L.latLngBounds(pts);
      if (b.isValid()) map.fitBounds(b, { padding: [30, 30], maxZoom: 14, animate: false });
    }
    const t = setTimeout(() => mapRef.current && map.invalidateSize(), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [located, homeBase]);

  // highlight active: swap icon + reveal (de-cluster) + scroll row
  useEffect(() => {
    Object.entries(markers.current).forEach(([id, { m, kind }]) => {
      m.setIcon(markerIcon(kind, id === activeId));
      if (id === activeId) m.setZIndexOffset(800); else m.setZIndexOffset(0);
    });
    const g = located.find((x) => x.id === activeId);
    if (g && clusterRef.current && markers.current[activeId]) {
      clusterRef.current.zoomToShowLayer(markers.current[activeId].m, () => {});
    }
    if (activeId && rowRefs.current[activeId]) rowRefs.current[activeId].scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeId, located]);

  const step = (dir) => {
    if (located.length === 0) return;
    const i = located.findIndex((g) => g.id === activeId);
    const next = i < 0 ? (dir > 0 ? 0 : located.length - 1) : (i + dir + located.length) % located.length;
    setActiveId(located[next].id);
  };

  const active = located.find((g) => g.id === activeId);

  const fitAll = () => {
    const map = mapRef.current; if (!map || located.length === 0) return;
    const b = L.latLngBounds(located.map((g) => [g.latitude, g.longitude]));
    if (b.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 15, animate: true });
  };
  const goMe = () => { const map = mapRef.current; if (meRef.current && map) map.setView(meRef.current.getLatLng(), 15); };
  const goSelected = () => { if (activeId && clusterRef.current && markers.current[activeId]) clusterRef.current.zoomToShowLayer(markers.current[activeId].m, () => {}); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid={testId}>
      {/* List */}
      <div className="lg:col-span-5 order-2 lg:order-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold uppercase text-[#6B7280]">{located.length} on map</span>
          <div className="flex gap-1">
            <button onClick={() => step(-1)} data-testid="explorer-prev" className="w-9 h-9 rounded-md border border-[#D1D5DB] bg-white flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => step(1)} data-testid="explorer-next" className="w-9 h-9 rounded-md border border-[#D1D5DB] bg-white flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="space-y-2 max-h-[62vh] overflow-y-auto pr-1" data-testid="explorer-list">
          {located.map((g) => {
            const mine = (g._kind || (g.status === "group_created" ? "mine" : "prospect")) === "mine";
            return (
              <div key={g.id} ref={(n) => (rowRefs.current[g.id] = n)} data-testid={`explorer-row-${g.id}`}
                onClick={() => setActiveId(g.id)}
                className={`border rounded-lg p-2.5 cursor-pointer ${g.id === activeId ? "border-[#1E3A8A] bg-[#EEF2FF] ring-1 ring-[#1E3A8A]" : "border-[#E5E7EB] bg-white hover:border-[#93C5FD]"}`}>
                <div className="flex items-center gap-2">
                  {mine ? <Store className="w-4 h-4 text-[#047857] shrink-0" /> : <Radar className="w-4 h-4 text-[#1E3A8A] shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-[#111827] truncate">{g.name}</div>
                    <div className="text-xs text-[#6B7280] truncate">{g.area}</div>
                  </div>
                  {!mine && g.opportunity_score != null && g.prospect_tier && (
                    <span className="text-xs font-black text-[#111827]">{g.opportunity_score}</span>
                  )}
                </div>
                <div className="mt-1.5 pl-6 text-[11px] text-[#6B7280] flex flex-wrap gap-x-3 gap-y-0.5">
                  {mine ? (
                    <>
                      <span>Last visit {dateWithAgo(g.last_visit_date, "never")}</span>
                      <span>{g.visit_count || 0} visits</span>
                      <span>{g.purchase_count || 0} orders</span>
                      <span className="font-bold text-[#047857]">{money(g.total_purchase_value)}</span>
                    </>
                  ) : (
                    <>
                      {g.display_status && <span className="capitalize">{g.display_status.replace("_", " ")}</span>}
                      <span>Last visit {dateWithAgo(g.last_visit_date, "never")}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {located.length === 0 && <p className="text-sm text-[#9CA3AF] py-6 text-center">Nothing with a location to show.</p>}
        </div>
      </div>

      {/* Map */}
      <div className="lg:col-span-7 order-1 lg:order-2 relative">
        <div ref={el} data-testid="explorer-map" className={`w-full ${heightClass} rounded-xl border border-[#E5E7EB] z-0`} />
        <div className="absolute bottom-4 right-4 z-[400] flex flex-col gap-2" data-testid="map-controls">
          <button onClick={fitAll} data-testid="explorer-fit-all" title="Fit all" aria-label="Fit all pins"
            className="w-11 h-11 rounded-full bg-white border border-[#E5E7EB] shadow-md flex items-center justify-center text-[#1E3A8A] active:scale-95"><Maximize className="w-5 h-5" /></button>
          <button onClick={goMe} data-testid="explorer-locate" title="My location" aria-label="Centre on my location"
            className="w-11 h-11 rounded-full bg-white border border-[#E5E7EB] shadow-md flex items-center justify-center text-[#2563EB] active:scale-95"><LocateFixed className="w-5 h-5" /></button>
          <button onClick={goSelected} disabled={!active} data-testid="explorer-go-selected" title="Selected garage" aria-label="Jump to selected garage"
            className="w-11 h-11 rounded-full bg-white border border-[#E5E7EB] shadow-md flex items-center justify-center text-[#047857] active:scale-95 disabled:opacity-40"><Crosshair className="w-5 h-5" /></button>
        </div>
        {active && (
          <div className="absolute left-3 right-3 bottom-3 md:left-3 md:right-auto md:w-[320px] z-[400] bg-white rounded-xl border border-[#E5E7EB] shadow-lg p-3" data-testid={`explorer-card-${active.id}`}>
            <div className="font-bold text-[#111827] truncate">{active.name}</div>
            <div className="text-xs text-[#6B7280] truncate">{active.area}</div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <span className="text-[#6B7280]">Last visit</span><span className="font-bold text-right">{dateWithAgo(active.last_visit_date, "never")}</span>
              <span className="text-[#6B7280]">Visits</span><span className="font-bold text-right">{active.visit_count || 0}</span>
              {active.status === "group_created" && (<><span className="text-[#6B7280]">Sales</span><span className="font-bold text-right">{money(active.total_purchase_value)}</span></>)}
            </div>
            <div className="mt-3 flex gap-2">
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${active.latitude},${active.longitude}`} target="_blank" rel="noopener noreferrer"
                data-testid={`explorer-navigate-${active.id}`} className="flex-1 h-10 rounded-lg border-2 border-[#1E3A8A] text-[#1E3A8A] text-sm font-bold flex items-center justify-center gap-1.5"><Navigation className="w-4 h-4" /> Navigate</a>
              <button onClick={() => onOpenDetail?.(active.id)} data-testid={`explorer-open-${active.id}`}
                className="flex-1 h-10 rounded-lg bg-[#1E3A8A] text-white text-sm font-bold flex items-center justify-center gap-1.5"><Info className="w-4 h-4" /> Details</button>
            </div>
          </div>
        )}
        {/* legend */}
        <div className="absolute top-3 right-3 z-[400] bg-white/95 rounded-lg border border-[#E5E7EB] shadow-sm px-2.5 py-1.5 text-[11px] flex items-center gap-3">
          <span className="flex items-center gap-1"><Home className="w-3.5 h-3.5 text-[#111827]" /> Home</span>
          <span className="flex items-center gap-1"><Store className="w-3.5 h-3.5 text-[#047857]" /> My garage</span>
          <span className="flex items-center gap-1"><Radar className="w-3.5 h-3.5 text-[#1E3A8A]" /> Prospect</span>
        </div>
      </div>
    </div>
  );
}
